import { Button } from '@jouzen/ecom-components/v3/Button';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useReducedMotion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

import { membershipInfoFeaturesData } from '@/app/[locale]/membership/_data/membershipInfoFeaturesData';
import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';
import { LEGAL_FOOTNOTES_ID } from '@/app/consts/ids';

const footnoteLink = () => (
  <sup>
    <a href={`#${LEGAL_FOOTNOTES_ID}`}>**</a>
  </sup>
);

const MembershipInfoFeatureGrid = () => {
  const t = useTranslations();
  const shouldReduceMotion = useReducedMotion();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const scrollRef = useRef<null | HTMLButtonElement>(null);
  const numberVisible = 6;

  useEffect(() => {
    if (isLoaded && scrollRef.current !== null) {
      scrollRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
      setIsLoaded(false);
    }
  }, [isLoaded]);

  return (
    <section
      className="relative pb-20 md:px-5 lg:pb-12"
      data-cy="membership-info-feature-grid"
    >
      <LayoutGrid className="h-full content-between">
        <GridItem
          colStart={{ sm: 'main', md: 3, lg: 3 }}
          colEnd={{ sm: 'main', md: 23, lg: 7 }}
        >
          <Typography
            Element="h3"
            variant="h3"
            className="text-4xl"
            height="tight"
          >
            {t('membership_features_and_so_much_more')}
          </Typography>
        </GridItem>
        <GridItem
          colStart={{ sm: 'main', md: 3, lg: 9 }}
          colEnd={{ sm: 'main', md: 23, lg: 23 }}
        >
          <div
            className="grid grid-cols-1 gap-x-20 gap-y-10 md:grid-cols-2 lg:gap-x-36 lg:gap-y-14"
            data-cy="membership-features-info-grid-container"
          >
            {membershipInfoFeaturesData.map((feature, i) => (
              <div key={feature.id} hidden={!isOpen && i >= numberVisible}>
                {isOpen && i >= numberVisible ? (
                  <Motion
                    animate={{
                      opacity: 1,
                      filter: 'blur(0px)',
                      y: 0,
                      transition: {
                        type: 'tween',
                        duration: shouldReduceMotion ? 0 : 0.8,
                        staggerChildren: shouldReduceMotion ? 0 : 0.1,
                      },
                    }}
                    initial={{ opacity: 0, filter: 'blur(5px)', y: -5 }}
                    transition={{
                      type: 'spring',
                      stiffness: 50,
                      duration: 0.5,
                    }}
                  >
                    <Typography
                      Element="h4"
                      variant="h6"
                      className="mb-3"
                      weight="normal"
                    >
                      {t(feature.title)}
                    </Typography>
                    <Typography>{t(feature.body)}</Typography>
                  </Motion>
                ) : (
                  <>
                    <Typography
                      Element="h4"
                      variant="h6"
                      className="mb-3"
                      weight="normal"
                    >
                      {t(feature.title)}
                    </Typography>
                    <Typography>
                      <RichText
                        messageKey={feature.body}
                        tValues={{ footnoteLink }}
                      />
                    </Typography>
                  </>
                )}
              </div>
            ))}
          </div>
        </GridItem>
        <GridItem
          colStart={{ sm: 'main', md: 3, lg: 9 }}
          colEnd={{ sm: 'main', md: 23, lg: 23 }}
        >
          {membershipInfoFeaturesData.length > numberVisible && (
            <Button
              onClick={() => {
                setIsOpen((prevIsOpen) => !prevIsOpen);
                setIsLoaded(true);
              }}
              variant="secondary-dark"
              className="mt-14"
              data-cy="membership-more-info-cta"
              ref={scrollRef}
            >
              {t(
                `membership_features_${isOpen ? 'close' : 'view_all_features'}`,
              )}
            </Button>
          )}
        </GridItem>
      </LayoutGrid>
    </section>
  );
};
export default MembershipInfoFeatureGrid;
